import {
  Component,
  DestroyRef,
  forwardRef,
  inject,
  OnInit,
  signal,
  computed,
  effect,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { SelectModule } from 'primeng/select';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { TranslationService } from '../../services/translation.service';

interface DropdownOption {
  label: string;
  value: number;
}

@Component({
  selector: 'app-date-of-birth-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, SelectModule, TranslatePipe],
  templateUrl: './date-of-birth-picker.component.html',
  styleUrl: './date-of-birth-picker.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateOfBirthPickerComponent),
      multi: true,
    },
  ],
})
export class DateOfBirthPickerComponent implements ControlValueAccessor, OnInit {
  selectedDay = signal<number | null>(null);
  selectedMonth = signal<number | null>(null);
  selectedYear = signal<number | null>(null);
  touched = signal(false);
  disabled = signal(false);

  monthOptions: DropdownOption[] = [];
  yearOptions: DropdownOption[] = [];

  dayOptions = computed(() => {
    const month = this.selectedMonth();
    const year = this.selectedYear();
    const maxDay = this.getDaysInMonth(month, year);
    const options: DropdownOption[] = [];
    for (let d = 1; d <= maxDay; d++) {
      options.push({ label: d.toString(), value: d });
    }
    return options;
  });

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  private readonly translationService = inject(TranslationService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      const day = this.selectedDay();
      const month = this.selectedMonth();
      const year = this.selectedYear();

      if (day && month && year) {
        // Clamp day if month changed and day exceeds max
        const maxDay = this.getDaysInMonth(month, year);
        if (day > maxDay) {
          this.selectedDay.set(maxDay);
          return; // effect will re-run with clamped value
        }

        const mm = month.toString().padStart(2, '0');
        const dd = day.toString().padStart(2, '0');
        this.onChange(`${year}-${mm}-${dd}`);
      } else {
        this.onChange(null);
      }
    });
  }

  ngOnInit(): void {
    this.monthOptions = this.buildMonthOptions();
    this.yearOptions = this.buildYearOptions();
    // Rebuild month labels whenever the user toggles language so the
    // dropdown matches the active locale.
    this.translationService.currentLanguage$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.monthOptions = this.buildMonthOptions();
      });
  }

  writeValue(value: string | Date | null): void {
    if (!value) {
      this.selectedDay.set(null);
      this.selectedMonth.set(null);
      this.selectedYear.set(null);
      return;
    }

    let dateStr: string;
    if (value instanceof Date) {
      dateStr = value.toISOString().split('T')[0];
    } else {
      dateStr = value.split('T')[0];
    }

    const parts = dateStr.split('-');
    if (parts.length === 3) {
      this.selectedYear.set(parseInt(parts[0], 10));
      this.selectedMonth.set(parseInt(parts[1], 10));
      this.selectedDay.set(parseInt(parts[2], 10));
    }
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  markTouched(): void {
    if (!this.touched()) {
      this.touched.set(true);
      this.onTouched();
    }
  }

  private buildMonthOptions(): DropdownOption[] {
    const months = [
      'January', 'February', 'March', 'April',
      'May', 'June', 'July', 'August',
      'September', 'October', 'November', 'December',
    ];
    return months.map((name, i) => ({
      label: this.translationService.translateDays(name, 'MONTHS_SINGLE'),
      value: i + 1,
    }));
  }

  private buildYearOptions(): DropdownOption[] {
    const currentYear = new Date().getFullYear();
    const options: DropdownOption[] = [];
    for (let y = currentYear; y >= 1920; y--) {
      options.push({ label: y.toString(), value: y });
    }
    return options;
  }

  private getDaysInMonth(month: number | null, year: number | null): number {
    if (!month) return 31;
    const y = year ?? 2000; // Use leap year as default
    return new Date(y, month, 0).getDate();
  }
}
